'use client';

import type { CSSProperties } from 'react';
import { VpIcon } from '@vtmn-play/react/headless';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { useUserDataContext } from '@/app/components/Provider';
import { UserDataProfileCompletion } from '@/app/hooks/useUserData';
import { usePathname } from '@/i18n/routing';
import { ActiveLink } from '~/components/commons/ActiveLink/ActiveLink';
import appConst from '~/const/appConst';
import { ProfileCompletionIcon } from './ProfileCompletionIcon';
import { ProfilePicture } from './ProfilePicture';

export const ProfileNavigation = ({
  onClick,
}: {
  onClick: (value: boolean) => void;
},
) => {
  const pathname = usePathname();
  const t = useTranslations('PROFILE');
  const { userData } = useUserDataContext();

  const { profile, sports_profile, favourite_sports, measurement_profile, digital_usage_profile, children_profile } = appConst.staticRoutes;
  const { CHILDREN, CONTACT_DETAILS, PROFILE, SPORTS, MEASUREMENT, DIGITAL_USAGE } = UserDataProfileCompletion;

  const data = [
    {
      icon: <VpIcon name="send-plane-top" />,
      label: t('CONTENT.PERSONAL_DATA'),
      route: profile,
      user_data_dto_key: CONTACT_DETAILS,
    },
    {
      icon: <VpIcon name="user" />,
      label: t('NAVIGATION.SPORT_PROFILE'),
      route: sports_profile,
      user_data_dto_key: PROFILE,
    },
    {
      icon: <VpIcon name="riding" />,
      label: t('NAVIGATION.FAVOURITE_SPORTS'),
      route: favourite_sports,
      user_data_dto_key: SPORTS,
    },
    {
      icon: <VpIcon name="ruler" />,
      label: t('CONTENT.MEASUREMENT'),
      route: measurement_profile,
      user_data_dto_key: MEASUREMENT,
    },
    {
      icon: <VpIcon name="devices" />,
      label: t('CONTENT.DIGITAL_USAGE'),
      route: digital_usage_profile,
      user_data_dto_key: DIGITAL_USAGE,
    },
    {
      icon: <VpIcon name="emotion-laugh" />,
      label: t('NAVIGATION.CHILDREN'),
      route: children_profile,
      user_data_dto_key: CHILDREN,
    },
  ];

  function getStickerStyle(key: UserDataProfileCompletion): CSSProperties {
    const value = userData.profile_completion[key];
    let color: string = null;

    if ([SPORTS, CHILDREN].includes(key))
      return {};

    if (value < 25)
      color = '#E9ABAD';
    else if (value < 75)
      color = '#E9C49F';
    else if (value < 100)
      color = '#FCEBBE';
    else
      color = '#ACD6C2';

    return {
      '--sticker-background-color': color,
    } as CSSProperties;
  }

  const percentage = useMemo(() => {
    const categories = [CONTACT_DETAILS, PROFILE, MEASUREMENT, DIGITAL_USAGE];

    if (!userData?.profile_completion)
      return 0;

    return Math.round(categories.reduce(
      (acc, current) => acc + userData.profile_completion[current],
      0,
    ) / categories.length);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.profile_completion]);

  return (
    <aside className="profile__aside">
      <nav className="profile__nav">
        <ul className="vp-link-list vp-link-list--small">
          <ProfilePicture />
          {data.map(({ route, label, icon, user_data_dto_key }) => (
            <li key={route} className="vp-link-list__item">
              <ActiveLink
                data-testid={`profile-completion-${user_data_dto_key}`}
                className="vp-link-list__item-link"
                href={route}
                {...pathname === route && { onClick: () => onClick(true) }}
              >
                <span className="profile__link-list__item">
                  <span className="profile__link-list__item__label">
                    {icon}
                    {label}
                  </span>
                  {typeof userData?.profile_completion?.[user_data_dto_key] === 'number' && (
                    <span
                      data-testid={`profile-completion-${user_data_dto_key}-sticker`}
                      className="vp-sticker vp-sticker--secondary vp-sticker--small"
                      style={getStickerStyle(user_data_dto_key)}
                    >
                      {userData.profile_completion?.[user_data_dto_key]}
                      {![SPORTS, CHILDREN].includes(user_data_dto_key) && '%'}
                    </span>
                  )}
                </span>
                <VpIcon name="chevron-right" size={20} />
              </ActiveLink>
            </li>
          ))}
        </ul>
      </nav>
      <div className="profile__aside__completion">
        <p className="vp-subtitle-m">
          {percentage < 100
            ? (
                t('NAVIGATION.COMPLETION.PROGRESS.TITLE', { count: percentage })
              )
            : (
                t('NAVIGATION.COMPLETION.PROGRESS.COMPLETED')
              )}
        </p>
        <div className="profile__aside__completion__progress">
          <div data-testid="user-data-completion-progress-bar" className="profile__aside__completion__progress__bar" style={{ width: `${percentage}%` }} />
        </div>
        <div className="profile__aside__completion__wrapper">
          <p className="vp-subtitle-m">
            <ProfileCompletionIcon />
            {t('NAVIGATION.COMPLETION.TIPS.TITLE')}
          </p>
          <p className="vp-body-s">
            {t('NAVIGATION.COMPLETION.TIPS.DESCRIPTION')}
          </p>
        </div>
      </div>
    </aside>
  );
};
